import { useReducer } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  TextField,
  Button,
  Modal,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../axiosInstance";
import { weddingFormReducer, initialState } from "./weddingFormReducer";

const ModalContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  backgroundColor: "#ffffff",
  borderRadius: 25,
  boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
  padding: `${theme.spacing(4)} ${theme.spacing(6)} ${theme.spacing(0)}`,
}));

const PrimaryHeading = styled(Typography)(({ theme, isDisabled = false }) => ({
  fontSize: 20,
  fontWeight: 400,
  lineHeight: "24px",
  marginBottom: "20px",
  color: isDisabled ? "#1D1B201A" : theme.palette.text.primary,
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.spacing(2),
  marginTop: theme.spacing(4),
}));

const StyledInput = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#F6F3F4",
    borderRadius: 8,
    fontWeight: 400,
    "& fieldset": {
      border: "none",
    },
    "&:hover fieldset": {
      border: "none",
    },
    "&.Mui-focused fieldset": {
      border: "none",
    },
    "&.Mui-error fieldset": {
      border: "none",
    },
  },
}));

const DescriptionContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  backgroundColor: `${theme.palette.custom.primaryPurple}CC`,
  color: theme.palette.common.white,
  padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
  borderRadius: 25,
  fontSize: 16,
  fontWeight: 400,
  lineHeight: "24px",
}));

const BackdropStyle = {
  backdropFilter: "blur(5px)",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
};

const NewWeddingModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(weddingFormReducer, initialState);
  const { weddingTitle, isTitleError, titleErrorText, isSubmitting } = state;

  const isWeddingTitleEmpty = !weddingTitle.trim();

  const handleClose = () => {
    dispatch({ type: "RESET_FORM" });
    onClose();
  };

  const handleSave = () => {
    if (isWeddingTitleEmpty) {
      dispatch({
        type: "SET_ERROR",
        payload: "Wedding title is required",
      });
      return;
    }

    dispatch({ type: "SET_SUBMITTING", payload: true });

    const Req = {
      method: "POST",
      baseURL:
        window.localStorage.getItem("BaseURL") +
        "/services/projectmanagement/api/projects",
      headers: {
        Authorization: "Bearer " + window.localStorage.getItem("Token"),
      },
      data: {
        name: weddingTitle,
      },
    };

    axiosInstance(Req)
      .then((Res) => {
        window.localStorage.setItem("CreatedWeddingName", weddingTitle);
        dispatch({ type: "RESET_FORM" });
        const weddingId = Res.data.id;
        window.localStorage.setItem("CreatedWeddingId", weddingId);
        navigate("/Home/AI_Studio/" + weddingId);
      })
      .catch((Err) => {
        if (Err.response?.status === 400 || Err.response?.status === 500) {
          dispatch({
            type: "SET_ERROR",
            payload: "Wedding with this title already exists",
          });
        }
        dispatch({ type: "SET_SUBMITTING", payload: false });
      });
  };

  const handleTitleChange = (e) => {
    dispatch({ type: "SET_TITLE", payload: e.target.value });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="new-wedding-modal-title"
      aria-describedby="new-wedding-modal-description"
      BackdropProps={{
        sx: BackdropStyle,
      }}
    >
      <ModalContainer>
        <PrimaryHeading variant="h6" id="new-wedding-modal-title">
          Create a new gallery
        </PrimaryHeading>

        <FormControl
          fullWidth
          error={isTitleError}
          id="new-wedding-modal-description"
        >
          <StyledInput
            fullWidth
            id="wedding-title-input"
            value={weddingTitle}
            onChange={handleTitleChange}
            placeholder="Add a gallery title..."
            error={isTitleError}
            autoFocus
            aria-invalid={isTitleError}
            aria-describedby={
              isTitleError ? "wedding-title-error" : "wedding-title-helper"
            }
          />
          {isTitleError && (
            <FormHelperText error id="wedding-title-error">
              {titleErrorText}
            </FormHelperText>
          )}
          <DescriptionContainer>
            You can add images, and all your creations on WeddinglyAi to a
            gallery.
          </DescriptionContainer>
        </FormControl>

        <ButtonContainer>
          <Button
            variant="text"
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label="Cancel creating new wedding"
            sx={{
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <PrimaryHeading variant="h6" sx={{ fontWeight: 500 }}>
              Cancel
            </PrimaryHeading>
          </Button>
          <Button
            variant="text"
            onClick={handleSave}
            disabled={isSubmitting || isWeddingTitleEmpty}
            aria-label="Save new wedding"
            sx={{
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <PrimaryHeading
              variant="h6"
              isDisabled={isSubmitting || isWeddingTitleEmpty}
              sx={{ fontWeight: 600 }}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </PrimaryHeading>
          </Button>
        </ButtonContainer>
      </ModalContainer>
    </Modal>
  );
};

export default NewWeddingModal;
